/**
 * Created by neo on 11.01.2026
 * Terra OAuth Callback Handler - Handles redirect from Terra OAuth flow
 */
import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from '@emotion/styled';
import { Theme } from '../../Theme/Theme';
import { useTrans } from '../../Store/System/LocalizationStore';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 24px;
  background: ${Theme.Colors.background};
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  padding: 48px;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  max-width: 400px;
`;

const Icon = styled.div<{ success?: boolean }>`
  font-size: 48px;
  margin-bottom: 16px;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: ${Theme.Colors.textColor};
  margin: 0 0 8px 0;
`;

const Message = styled.p`
  font-size: 14px;
  color: ${Theme.Colors.charcoal};
  margin: 0;
  line-height: 1.5;
`;

export const TerraCallbackScreen: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { t } = useTrans();

  const result = searchParams.get('result');
  const resource = searchParams.get('resource');
  const isSuccess = result === 'success';

  useEffect(() => {
    // Send message to opener window (if opened in popup)
    if (window.opener) {
      window.opener.postMessage(
        {
          type: 'TERRA_AUTH_CALLBACK',
          result: result,
          provider: resource,
        },
        window.location.origin,
      );

      // Close popup after a short delay
      setTimeout(() => {
        window.close();
      }, 2000);
    }
  }, [result, resource]);

  return (
    <Container>
      <Card>
        <Icon>{isSuccess ? '✅' : '❌'}</Icon>
        <Title>
          {isSuccess
            ? t('trackers.callback.successTitle', { defaultValue: 'Tracker Connected!' })
            : t('trackers.callback.errorTitle', { defaultValue: 'Connection Failed' })}
        </Title>
        <Message>
          {isSuccess
            ? t('trackers.callback.successMessage', {
                defaultValue: 'Your tracker has been connected successfully. This window will close automatically.',
              })
            : t('trackers.callback.errorMessage', {
                defaultValue: 'There was a problem connecting your tracker. Please try again.',
              })}
        </Message>
      </Card>
    </Container>
  );
};
